import {
  resolveWearablePathDefaultsForTrack,
  type WearablePathDefaults,
  type WearablePathTrack,
} from "./wearables.pathDefaults";

export type WearablePathOverrideSurface = "guided-studio" | "omnistudio" | "xr";

export type WearablePathOverrideMode = "pinned" | "dismissed";

export type WearablePathOverrideEntry = {
  mode: WearablePathOverrideMode;
  track?: WearablePathTrack | null;
  updatedAt: string;
};

export type WearablePathOverrideMemory = Partial<
  Record<WearablePathOverrideSurface, WearablePathOverrideEntry | null>
>;

export type ResolvedWearablePathOverride = {
  effectiveDefaults: WearablePathDefaults;
  baseTrack: WearablePathTrack;
  effectiveTrack: WearablePathTrack;
  overrideMode: "inherit" | WearablePathOverrideMode;
  isUserOverride: boolean;
  stateLabel: string;
  stateDescription: string;
  continuityChips: string[];
};

export function resolveWearablePathOverride(
  baseDefaults: WearablePathDefaults,
  overrideEntry?: WearablePathOverrideEntry | null,
  recommendedSurfaceIds: string[] = [],
): ResolvedWearablePathOverride {
  if (!overrideEntry) {
    return {
      effectiveDefaults: baseDefaults,
      baseTrack: baseDefaults.track,
      effectiveTrack: baseDefaults.track,
      overrideMode: "inherit",
      isUserOverride: false,
      stateLabel: "Inherited profile guidance",
      stateDescription:
        "Entry emphasis follows your wearable profile continuity unless you explicitly override it.",
      continuityChips: baseDefaults.continuityChips,
    };
  }

  if (overrideEntry.mode === "dismissed") {
    const effectiveDefaults = resolveWearablePathDefaultsForTrack(
      "general",
      "override-dismissed",
      recommendedSurfaceIds,
    );

    return {
      effectiveDefaults: {
        ...effectiveDefaults,
        advisory: {
          ...effectiveDefaults.advisory,
          summary:
            "You dismissed path-aware entry guidance for this surface. Neutral ordering is being used instead.",
          overrideLabel:
            "This reset affects entry emphasis only and does not alter wearable truth.",
        },
      },
      baseTrack: baseDefaults.track,
      effectiveTrack: "general",
      overrideMode: "dismissed",
      isUserOverride: true,
      stateLabel: "Dismissed for this surface",
      stateDescription:
        "Path-aware entry guidance is suppressed here until you reset it.",
      continuityChips: [
        "User dismissed guidance",
        "Neutral ordering",
        "Does not change wearable truth",
        "Reset anytime",
      ],
    };
  }

  if (overrideEntry.mode === "pinned" && overrideEntry.track) {
    const effectiveDefaults = resolveWearablePathDefaultsForTrack(
      overrideEntry.track,
      "override-pinned",
      recommendedSurfaceIds,
    );

    return {
      effectiveDefaults: {
        ...effectiveDefaults,
        advisory: {
          ...effectiveDefaults.advisory,
          summary:
            "You pinned this entry preference for the current surface. It remains advisory and surface-scoped.",
          overrideLabel:
            "Pinned entry emphasis does not rewrite wearable baseline, profile bridge, or onboarding truth.",
        },
      },
      baseTrack: baseDefaults.track,
      effectiveTrack: overrideEntry.track,
      overrideMode: "pinned",
      isUserOverride: true,
      stateLabel: "Pinned by you",
      stateDescription:
        "This surface now prefers your explicit pinned path until you reset it.",
      continuityChips: [
        `Pinned path: ${overrideEntry.track}`,
        "User entry preference",
        "Surface-scoped memory",
        "Reset anytime",
      ],
    };
  }

  return {
    effectiveDefaults: baseDefaults,
    baseTrack: baseDefaults.track,
    effectiveTrack: baseDefaults.track,
    overrideMode: "inherit",
    isUserOverride: false,
    stateLabel: "Inherited profile guidance",
    stateDescription:
      "Entry emphasis follows your wearable profile continuity unless you explicitly override it.",
    continuityChips: baseDefaults.continuityChips,
  };
}